import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity,
  Clock,
  Users,
  AlertTriangle,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Search,
  Filter,
  X,
  Shield,
  TrendingUp,
  TrendingDown,
  Globe,
  Monitor,
  Server,
  Briefcase,
  Loader2,
  ArrowUpRight,
  ArrowDownRight,
  ChevronLeft,
} from 'lucide-react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';

// ============================================================
// Types
// ============================================================

interface ActivityLog {
  id: string;
  timestamp: string;
  logType: 'http_request' | 'frontend_event' | 'backend_error' | 'business_action';
  userId?: string;
  userName?: string;
  userEmail?: string;
  action: string;
  description?: string;
  path?: string;
  method?: string;
  statusCode?: number;
  duration?: number;
  severity: 'debug' | 'info' | 'warning' | 'error' | 'critical';
  metadata?: Record<string, unknown>;
  requestBody?: Record<string, unknown>;
  responseBody?: Record<string, unknown>;
  queryParams?: Record<string, unknown>;
  headers?: Record<string, unknown>;
  errorMessage?: string;
  stackTrace?: string;
  pageUrl?: string;
  component?: string;
  eventData?: Record<string, unknown>;
  breadcrumbs?: string[];
  beforeState?: Record<string, unknown>;
  afterState?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
}

interface ActivityLogStats {
  totalActivities: number;
  totalActivitiesTrend: number;
  activeUsers: number;
  activeUsersTrend: number;
  creates: number;
  createsTrend: number;
  errors: number;
  errorsTrend: number;
}

interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface LogsResponse {
  success: boolean;
  data: ActivityLog[];
  pagination: PaginationInfo;
  tabCounts?: Record<string, number>;
}

interface StatsResponse {
  success: boolean;
  data: ActivityLogStats;
}

type TabFilter = 'all' | 'http_request' | 'frontend_event' | 'backend_error' | 'business_action';
type TimeRange = '1h' | '6h' | '24h' | '7d' | '30d';
type SeverityFilter = 'all' | 'debug' | 'info' | 'warning' | 'error' | 'critical';

// ============================================================
// Constants
// ============================================================

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const TAB_OPTIONS: { value: TabFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'http_request', label: 'API Requests' },
  { value: 'frontend_event', label: 'Frontend Events' },
  { value: 'backend_error', label: 'Backend Errors' },
  { value: 'business_action', label: 'User Actions' },
];

const TIME_RANGE_OPTIONS: { value: TimeRange; label: string }[] = [
  { value: '1h', label: 'Last 1h' },
  { value: '6h', label: 'Last 6h' },
  { value: '24h', label: 'Last 24h' },
  { value: '7d', label: 'Last 7d' },
  { value: '30d', label: 'Last 30d' },
];

const SEVERITY_OPTIONS: { value: SeverityFilter; label: string }[] = [
  { value: 'all', label: 'All Severities' },
  { value: 'debug', label: 'Debug' },
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'error', label: 'Error' },
  { value: 'critical', label: 'Critical' },
];

const SEVERITY_COLORS: Record<string, string> = {
  debug: 'bg-gray-100 text-gray-700 border-gray-200',
  info: 'bg-blue-50 text-blue-700 border-blue-200',
  warning: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  error: 'bg-red-50 text-red-700 border-red-200',
  critical: 'bg-purple-50 text-purple-700 border-purple-200',
};

const LOG_TYPE_COLORS: Record<string, string> = {
  http_request: 'bg-blue-50 text-blue-700 border-blue-200',
  frontend_event: 'bg-green-50 text-green-700 border-green-200',
  backend_error: 'bg-red-50 text-red-700 border-red-200',
  business_action: 'bg-purple-50 text-purple-700 border-purple-200',
};

const LOG_TYPE_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  http_request: Globe,
  frontend_event: Monitor,
  backend_error: Server,
  business_action: Briefcase,
};

const LOG_TYPE_LABELS: Record<string, string> = {
  http_request: 'API Request',
  frontend_event: 'Frontend',
  backend_error: 'Backend Error',
  business_action: 'User Action',
};

const STATUS_CODE_COLORS: Record<string, string> = {
  '2': 'text-green-600',
  '3': 'text-blue-600',
  '4': 'text-yellow-600',
  '5': 'text-red-600',
};

const ITEMS_PER_PAGE = 50;
const AUTO_REFRESH_INTERVAL = 15000;

// ============================================================
// Helper Functions
// ============================================================

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function formatRelativeTime(dateString: string): string {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatAbsoluteTime(dateString: string): string {
  return new Date(dateString).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function getStatusCodeColor(code: number): string {
  const prefix = Math.floor(code / 100).toString();
  return STATUS_CODE_COLORS[prefix] || 'text-gray-600';
}

// ============================================================
// Sub-components
// ============================================================

// -- Stat Card --
interface StatCardProps {
  title: string;
  value: number;
  trend: number;
  icon: React.ComponentType<{ className?: string }>;
  iconColor: string;
  isError?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, trend, icon: Icon, iconColor, isError }) => {
  // Backend stat values should be numbers, but coerce defensively so a stray
  // string never crashes .toLocaleString() / numeric comparisons.
  const numValue = typeof value === 'string' ? parseInt(value, 10) || 0 : (value ?? 0);
  const trendPositive = trend > 0;
  const trendColor = isError
    ? trend > 0
      ? 'text-red-600'
      : 'text-green-600'
    : trend > 0
    ? 'text-green-600'
    : trend < 0
    ? 'text-red-600'
    : 'text-gray-500';

  return (
    <Card className="border border-gray-200">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">{title}</p>
            <p className={`text-2xl font-bold mt-1 ${isError && numValue > 0 ? 'text-red-600' : 'text-gray-900'}`}>
              {numValue.toLocaleString()}
            </p>
          </div>
          <div className={`p-2.5 rounded-lg ${iconColor}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        {trend !== 0 && (
          <div className={`flex items-center mt-2 text-xs ${trendColor}`}>
            {trendPositive ? (
              <ArrowUpRight className="h-3 w-3 mr-0.5" />
            ) : (
              <ArrowDownRight className="h-3 w-3 mr-0.5" />
            )}
            <span>{Math.abs(trend)}% vs previous period</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// -- Loading Skeleton --
const LoadingSkeleton: React.FC = () => (
  <div className="space-y-4">
    {/* Stats skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <Card key={i} className="border border-gray-200">
          <CardContent className="p-4">
            <Skeleton className="h-3 w-24 mb-2" />
            <Skeleton className="h-8 w-16 mb-2" />
            <Skeleton className="h-3 w-32" />
          </CardContent>
        </Card>
      ))}
    </div>
    {/* Table skeleton */}
    <Card className="border border-gray-200">
      <CardContent className="p-0">
        <div className="p-4 border-b border-gray-100">
          <Skeleton className="h-8 w-full" />
        </div>
        {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
          <div key={i} className="p-4 border-b border-gray-50 flex items-center gap-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-5 w-24 rounded-full" />
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-48 flex-1" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </CardContent>
    </Card>
  </div>
);

// -- JSON Viewer --
const JsonViewer: React.FC<{ data: unknown; label?: string }> = ({ data, label }) => {
  if (data === null || data === undefined) return null;

  let formatted: string;
  try {
    formatted = JSON.stringify(data, null, 2);
  } catch {
    formatted = String(data);
  }

  return (
    <div className="mt-2">
      {label && <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>}
      <pre className="bg-gray-900 text-gray-100 text-xs p-3 rounded-lg overflow-x-auto max-h-64 overflow-y-auto font-mono leading-relaxed">
        {formatted.split('\n').map((line, idx) => {
          // Simple syntax highlighting for JSON
          const highlighted = line
            .replace(/"([^"]+)":/g, '<span class="text-purple-300">"$1"</span>:')
            .replace(/: "([^"]*)"/g, ': <span class="text-green-300">"$1"</span>')
            .replace(/: (\d+)/g, ': <span class="text-yellow-300">$1</span>')
            .replace(/: (true|false)/g, ': <span class="text-blue-300">$1</span>')
            .replace(/: (null)/g, ': <span class="text-gray-500">$1</span>');
          return (
            <span key={idx} dangerouslySetInnerHTML={{ __html: highlighted + '\n' }} />
          );
        })}
      </pre>
    </div>
  );
};

// -- State Diff Viewer --
const StateDiffViewer: React.FC<{
  before: Record<string, unknown>;
  after: Record<string, unknown>;
}> = ({ before, after }) => {
  const allKeys = Array.from(new Set([...Object.keys(before || {}), ...Object.keys(after || {})]));
  const changedKeys = allKeys.filter((key) => {
    return JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]);
  });

  if (changedKeys.length === 0) {
    return <p className="text-xs text-gray-500 italic">No changes detected</p>;
  }

  return (
    <div className="space-y-2 mt-2">
      <p className="text-xs font-medium text-gray-500">Changed Fields ({changedKeys.length})</p>
      <div className="bg-gray-50 rounded-lg border border-gray-200 divide-y divide-gray-200">
        {changedKeys.map((key) => (
          <div key={key} className="p-2.5 text-xs">
            <span className="font-mono font-medium text-gray-700">{key}</span>
            <div className="mt-1 flex flex-col gap-1">
              {before?.[key] !== undefined && (
                <div className="flex items-start gap-2">
                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 shrink-0">OLD</span>
                  <span className="font-mono text-red-600 break-all">
                    {typeof before[key] === 'object' ? JSON.stringify(before[key]) : String(before[key])}
                  </span>
                </div>
              )}
              {after?.[key] !== undefined && (
                <div className="flex items-start gap-2">
                  <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 shrink-0">NEW</span>
                  <span className="font-mono text-green-600 break-all">
                    {typeof after[key] === 'object' ? JSON.stringify(after[key]) : String(after[key])}
                  </span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// -- Expanded Row Detail --
const ExpandedDetail: React.FC<{ log: ActivityLog }> = ({ log }) => {
  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.2 }}
      className="bg-gray-50 border-t border-gray-100"
    >
      <div className="p-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left column - Common info */}
        <div className="space-y-3">
          <div>
            <p className="text-xs font-medium text-gray-500">Full Timestamp</p>
            <p className="text-sm text-gray-900">{formatAbsoluteTime(log.timestamp)}</p>
          </div>

          {log.description && (
            <div>
              <p className="text-xs font-medium text-gray-500">Description</p>
              <p className="text-sm text-gray-900">{log.description}</p>
            </div>
          )}

          {log.ipAddress && (
            <div>
              <p className="text-xs font-medium text-gray-500">IP Address</p>
              <p className="text-sm font-mono text-gray-900">{log.ipAddress}</p>
            </div>
          )}

          {log.userAgent && (
            <div>
              <p className="text-xs font-medium text-gray-500">User Agent</p>
              <p className="text-sm text-gray-700 break-all text-xs">{log.userAgent}</p>
            </div>
          )}

          {log.errorMessage && (
            <div>
              <p className="text-xs font-medium text-red-500">Error Message</p>
              <p className="text-sm text-red-700 bg-red-50 rounded p-2 font-mono">{log.errorMessage}</p>
            </div>
          )}
        </div>

        {/* Right column - Type-specific info */}
        <div className="space-y-3">
          {/* HTTP Request details */}
          {log.logType === 'http_request' && (
            <>
              {log.queryParams && Object.keys(log.queryParams).length > 0 && (
                <JsonViewer data={log.queryParams} label="Query Parameters" />
              )}
              {log.requestBody && Object.keys(log.requestBody).length > 0 && (
                <JsonViewer data={log.requestBody} label="Request Body" />
              )}
              {log.responseBody && Object.keys(log.responseBody).length > 0 && (
                <JsonViewer data={log.responseBody} label="Response Body" />
              )}
              {log.headers && Object.keys(log.headers).length > 0 && (
                <JsonViewer data={log.headers} label="Headers" />
              )}
            </>
          )}

          {/* Frontend Event details */}
          {log.logType === 'frontend_event' && (
            <>
              {log.pageUrl && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Page URL</p>
                  <p className="text-sm text-gray-900 font-mono break-all">{log.pageUrl}</p>
                </div>
              )}
              {log.component && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Component</p>
                  <p className="text-sm text-gray-900 font-mono">{log.component}</p>
                </div>
              )}
              {log.eventData && Object.keys(log.eventData).length > 0 && (
                <JsonViewer data={log.eventData} label="Event Data" />
              )}
              {log.breadcrumbs && log.breadcrumbs.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Breadcrumbs</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {log.breadcrumbs.map((crumb, i) => (
                      <span key={i} className="text-xs bg-gray-200 text-gray-700 rounded px-2 py-0.5">
                        {crumb}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Backend Error details */}
          {log.logType === 'backend_error' && (
            <>
              {log.stackTrace && (
                <div>
                  <p className="text-xs font-medium text-red-500">Stack Trace</p>
                  <pre className="bg-red-950 text-red-200 text-xs p-3 rounded-lg overflow-x-auto max-h-48 overflow-y-auto font-mono leading-relaxed mt-1">
                    {log.stackTrace}
                  </pre>
                </div>
              )}
              {log.breadcrumbs && log.breadcrumbs.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500">Breadcrumbs</p>
                  <div className="mt-1 space-y-1">
                    {log.breadcrumbs.map((crumb, i) => (
                      <p key={i} className="text-xs text-gray-700 font-mono bg-gray-100 rounded px-2 py-1">
                        {crumb}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Business Action details - state diff */}
          {log.logType === 'business_action' && log.beforeState && log.afterState && (
            <StateDiffViewer before={log.beforeState} after={log.afterState} />
          )}

          {/* Generic metadata fallback */}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <JsonViewer data={log.metadata} label="Metadata" />
          )}
        </div>
      </div>
    </motion.div>
  );
};

// -- Empty State --
const EmptyState: React.FC<{ message?: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-16">
    <Activity className="h-12 w-12 text-gray-300 mb-4" />
    <h3 className="text-lg font-medium text-gray-900 mb-1">No activity logs found</h3>
    <p className="text-sm text-gray-500">{message || 'Try adjusting your filters or time range.'}</p>
  </div>
);

// ============================================================
// Main Component
// ============================================================

const ActivityLogsPage: React.FC = () => {
  const { user } = useAuth();

  // State
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [stats, setStats] = useState<ActivityLogStats | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({ page: 1, limit: ITEMS_PER_PAGE, total: 0, pages: 0 });
  const [tabCounts, setTabCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isStatsLoading, setIsStatsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [activeTab, setActiveTab] = useState<TabFilter>('all');
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('all');
  const [userFilter, setUserFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);

  // UI state
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [uniqueUsers, setUniqueUsers] = useState<{ id: string; name: string }[]>([]);

  // Refs
  const autoRefreshRef = useRef<NodeJS.Timeout | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastRefreshRef = useRef<Date>(new Date());

  // ---- Data fetching ----

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', ITEMS_PER_PAGE.toString());
      params.append('timeRange', timeRange);

      if (activeTab !== 'all') {
        params.append('logType', activeTab);
      }
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      if (severityFilter !== 'all') {
        params.append('severity', severityFilter);
      }
      if (userFilter !== 'all') {
        params.append('userId', userFilter);
      }

      const response = await fetch(`${API_BASE_URL}/activity-logs?${params}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch activity logs: ${response.status} ${errorText}`);
      }

      const result: LogsResponse = await response.json();
      const logsData = result.data || [];
      setLogs(logsData);
      setPagination(result.pagination || { page: currentPage, limit: ITEMS_PER_PAGE, total: 0, pages: 0 });
      if (result.tabCounts) {
        setTabCounts(result.tabCounts);
      }

      // Extract unique users from log data for the user filter dropdown
      const usersMap = new Map<string, string>();
      logsData.forEach((log) => {
        if (log.userId && log.userName) {
          usersMap.set(log.userId, log.userName);
        }
      });
      setUniqueUsers((prev) => {
        const newUsers = Array.from(usersMap.entries()).map(([id, name]) => ({ id, name }));
        // Merge with existing to avoid losing users from other pages
        const merged = new Map<string, string>();
        prev.forEach((u) => merged.set(u.id, u.name));
        newUsers.forEach((u) => merged.set(u.id, u.name));
        return Array.from(merged.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name));
      });

      setError(null);
      lastRefreshRef.current = new Date();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch activity logs');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, timeRange, activeTab, searchQuery, severityFilter, userFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('timeRange', timeRange);

      const response = await fetch(`${API_BASE_URL}/activity-logs/stats?${params}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        // Stats endpoint might not exist yet - fail gracefully
        setStats({
          totalActivities: 0,
          totalActivitiesTrend: 0,
          activeUsers: 0,
          activeUsersTrend: 0,
          creates: 0,
          createsTrend: 0,
          errors: 0,
          errorsTrend: 0,
        });
        return;
      }

      const result: StatsResponse = await response.json();
      setStats(result.data || null);
    } catch {
      // Fail gracefully for stats
      setStats({
        totalActivities: 0,
        totalActivitiesTrend: 0,
        activeUsers: 0,
        activeUsersTrend: 0,
        creates: 0,
        createsTrend: 0,
        errors: 0,
        errorsTrend: 0,
      });
    } finally {
      setIsStatsLoading(false);
    }
  }, [timeRange]);

  const handleRefresh = useCallback(() => {
    setIsLoading(true);
    setIsStatsLoading(true);
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  // ---- Effects ----

  // Initial load + whenever filters change
  useEffect(() => {
    setIsLoading(true);
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    setIsStatsLoading(true);
    fetchStats();
  }, [fetchStats]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        fetchLogs();
        fetchStats();
      }, AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
    };
  }, [autoRefresh, fetchLogs, fetchStats]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setCurrentPage(1);
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setExpandedRows(new Set());
  }, [activeTab, timeRange, severityFilter, userFilter]);

  // ---- Handlers ----

  const toggleRowExpansion = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearAllFilters = () => {
    setSearchQuery('');
    setSeverityFilter('all');
    setUserFilter('all');
    setActiveTab('all');
    setTimeRange('24h');
    setCurrentPage(1);
    setExpandedRows(new Set());
  };

  const hasActiveFilters =
    searchQuery.trim() !== '' ||
    severityFilter !== 'all' ||
    userFilter !== 'all' ||
    activeTab !== 'all' ||
    timeRange !== '24h';

  // ---- Access Control ----

  if (user?.role !== 'CEO') {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
            <p className="text-gray-500">
              Activity Logs are only accessible to the CEO role.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // ---- Render ----

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* ==================== Header ==================== */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Activity Logs</h1>
            <p className="text-gray-500 mt-1">Real-time system activity monitoring</p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Auto-refresh toggle */}
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              aria-label={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh'}
              aria-pressed={autoRefresh}
              className={`
                flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all
                ${autoRefresh
                  ? 'bg-green-50 border-green-200 text-green-700 hover:bg-green-100'
                  : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                }
              `}
            >
              <span className="relative flex h-2 w-2">
                {autoRefresh && (
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                )}
                <span
                  className={`relative inline-flex rounded-full h-2 w-2 ${
                    autoRefresh ? 'bg-green-500' : 'bg-gray-400'
                  }`}
                />
              </span>
              <span>{autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}</span>
            </button>

            {/* Manual refresh */}
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoading}
              className="gap-1.5"
            >
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>

            {/* Time range selector */}
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
              {TIME_RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setTimeRange(opt.value)}
                  aria-label={`Filter by ${opt.label}`}
                  aria-pressed={timeRange === opt.value}
                  className={`
                    px-3 py-1.5 text-xs font-medium rounded-md transition-all
                    ${timeRange === opt.value
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                    }
                  `}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ==================== Stats Bar ==================== */}
        {isStatsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="border border-gray-200">
                <CardContent className="p-4">
                  <Skeleton className="h-3 w-24 mb-2" />
                  <Skeleton className="h-8 w-16 mb-2" />
                  <Skeleton className="h-3 w-32" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : stats ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Activities"
              value={stats.totalActivities ?? 0}
              trend={stats.totalActivitiesTrend ?? 0}
              icon={Activity}
              iconColor="bg-blue-100 text-blue-600"
            />
            <StatCard
              title="Active Users"
              value={stats.activeUsers ?? 0}
              trend={stats.activeUsersTrend ?? 0}
              icon={Users}
              iconColor="bg-indigo-100 text-indigo-600"
            />
            <StatCard
              title="Creates"
              value={stats.creates ?? 0}
              trend={stats.createsTrend ?? 0}
              icon={TrendingUp}
              iconColor="bg-green-100 text-green-600"
            />
            <StatCard
              title="Errors"
              value={stats.errors ?? 0}
              trend={stats.errorsTrend ?? 0}
              icon={AlertTriangle}
              iconColor="bg-red-100 text-red-600"
              isError
            />
          </div>
        ) : null}

        {/* ==================== Tab Filters ==================== */}
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 overflow-x-auto" role="tablist" aria-label="Log type filters">
          {TAB_OPTIONS.map((tab) => {
            const count = tab.value === 'all' ? pagination.total : (tabCounts[tab.value] ?? 0);
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                aria-label={`Show ${tab.label} logs`}
                aria-pressed={activeTab === tab.value}
                role="tab"
                className={`
                  flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-all
                  ${activeTab === tab.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                {tab.label}
                {count > 0 && (
                  <span
                    className={`
                      text-xs px-1.5 py-0.5 rounded-full
                      ${activeTab === tab.value
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-200 text-gray-600'
                      }
                    `}
                  >
                    {count > 999 ? '999+' : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ==================== Search & Filters ==================== */}
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search actions, paths, error messages..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search activity logs"
              className="w-full pl-10 pr-10 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {/* Filter toggles */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              aria-label={showFilters ? 'Hide filters' : 'Show filters'}
              aria-expanded={showFilters}
              className={`
                flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all
                ${showFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                }
              `}
            >
              <Filter className="h-4 w-4" />
              Filters
              {hasActiveFilters && (
                <span className="bg-blue-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  !
                </span>
              )}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearAllFilters}
                aria-label="Clear all filters"
                className="flex items-center gap-1 px-3 py-2.5 rounded-lg border border-gray-200 bg-white text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-all"
              >
                <X className="h-3.5 w-3.5" />
                Clear all
              </button>
            )}
          </div>
        </div>

        {/* Expanded filter bar */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                {/* Severity */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500">Severity</label>
                  <select
                    value={severityFilter}
                    onChange={(e) => setSeverityFilter(e.target.value as SeverityFilter)}
                    aria-label="Filter by severity"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {SEVERITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* User */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-gray-500">User</label>
                  <select
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    aria-label="Filter by user"
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[180px]"
                  >
                    <option value="all">All Users</option>
                    {uniqueUsers.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ==================== Error Banner ==================== */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" role="alert">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" aria-hidden="true" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">Failed to load activity logs</p>
              <p className="text-sm text-red-600 mt-0.5">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={handleRefresh} aria-label="Retry loading activity logs">
              Retry
            </Button>
          </div>
        )}

        {/* ==================== Main Table ==================== */}
        {isLoading && logs.length === 0 ? (
          <LoadingSkeleton />
        ) : logs.length === 0 ? (
          <Card className="border border-gray-200">
            <EmptyState
              message={
                hasActiveFilters
                  ? 'No logs match your current filters. Try broadening your search.'
                  : 'No activity logs recorded yet.'
              }
            />
          </Card>
        ) : (
          <Card className="border border-gray-200 overflow-hidden">
            {/* Table header */}
            <div className="hidden lg:grid grid-cols-[100px_120px_140px_1fr_160px_70px_80px_80px] gap-2 px-4 py-3 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <div>Time</div>
              <div>Type</div>
              <div>User</div>
              <div>Action</div>
              <div>Path</div>
              <div>Status</div>
              <div>Duration</div>
              <div>Severity</div>
            </div>

            {/* Table rows */}
            <div className="divide-y divide-gray-100">
              {logs.map((log) => {
                const isExpanded = expandedRows.has(log.id);
                const TypeIcon = LOG_TYPE_ICONS[log.logType] || Activity;

                return (
                  <div key={log.id}>
                    {/* Row */}
                    <button
                      onClick={() => toggleRowExpansion(log.id)}
                      aria-expanded={isExpanded}
                      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} log entry: ${log.action}`}
                      className="w-full text-left hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                    >
                      {/* Desktop row */}
                      <div className="hidden lg:grid grid-cols-[100px_120px_140px_1fr_160px_70px_80px_80px] gap-2 px-4 py-3 items-center">
                        {/* Time */}
                        <div className="flex items-center gap-1.5">
                          <motion.div
                            animate={{ rotate: isExpanded ? 90 : 0 }}
                            transition={{ duration: 0.15 }}
                          >
                            <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                          </motion.div>
                          <span
                            className="text-xs text-gray-600 cursor-help"
                            title={formatAbsoluteTime(log.timestamp)}
                          >
                            {formatRelativeTime(log.timestamp)}
                          </span>
                        </div>

                        {/* Type badge */}
                        <div>
                          <span
                            className={`
                              inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border
                              ${LOG_TYPE_COLORS[log.logType] || 'bg-gray-50 text-gray-700 border-gray-200'}
                            `}
                          >
                            <TypeIcon className="h-3 w-3" />
                            <span className="truncate">{LOG_TYPE_LABELS[log.logType] || log.logType}</span>
                          </span>
                        </div>

                        {/* User */}
                        <div className="text-sm text-gray-700 truncate" title={log.userEmail || ''}>
                          {log.userName || log.userEmail || 'System'}
                        </div>

                        {/* Action */}
                        <div className="text-sm text-gray-900 truncate" title={log.description || log.action}>
                          {log.method && (
                            <span className="text-xs font-mono font-medium text-gray-500 mr-1.5">
                              {log.method}
                            </span>
                          )}
                          {log.action}
                        </div>

                        {/* Path */}
                        <div className="text-xs font-mono text-gray-500 truncate" title={log.path || ''}>
                          {log.path || '-'}
                        </div>

                        {/* Status */}
                        <div>
                          {log.statusCode ? (
                            <span className={`text-sm font-mono font-medium ${getStatusCodeColor(log.statusCode)}`}>
                              {log.statusCode}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">-</span>
                          )}
                        </div>

                        {/* Duration */}
                        <div className="text-xs text-gray-500">
                          {log.duration !== undefined && log.duration !== null
                            ? formatDuration(log.duration)
                            : '-'}
                        </div>

                        {/* Severity */}
                        <div>
                          <span
                            className={`
                              inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border
                              ${SEVERITY_COLORS[log.severity] || 'bg-gray-100 text-gray-700 border-gray-200'}
                            `}
                          >
                            {log.severity}
                          </span>
                        </div>
                      </div>

                      {/* Mobile/Tablet row */}
                      <div className="lg:hidden px-4 py-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <motion.div
                              animate={{ rotate: isExpanded ? 90 : 0 }}
                              transition={{ duration: 0.15 }}
                            >
                              <ChevronRight className="h-3.5 w-3.5 text-gray-400" />
                            </motion.div>
                            <span
                              className={`
                                inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border
                                ${LOG_TYPE_COLORS[log.logType] || 'bg-gray-50 text-gray-700 border-gray-200'}
                              `}
                            >
                              <TypeIcon className="h-3 w-3" />
                              {LOG_TYPE_LABELS[log.logType] || log.logType}
                            </span>
                            <span
                              className={`
                                inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border
                                ${SEVERITY_COLORS[log.severity] || 'bg-gray-100 text-gray-700 border-gray-200'}
                              `}
                            >
                              {log.severity}
                            </span>
                          </div>
                          <span
                            className="text-xs text-gray-500"
                            title={formatAbsoluteTime(log.timestamp)}
                          >
                            {formatRelativeTime(log.timestamp)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-900">
                          {log.method && (
                            <span className="text-xs font-mono font-medium text-gray-500 mr-1.5">
                              {log.method}
                            </span>
                          )}
                          {log.action}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span>{log.userName || 'System'}</span>
                          {log.path && (
                            <>
                              <span className="text-gray-300">|</span>
                              <span className="font-mono truncate">{log.path}</span>
                            </>
                          )}
                          {log.statusCode && (
                            <>
                              <span className="text-gray-300">|</span>
                              <span className={`font-mono font-medium ${getStatusCodeColor(log.statusCode)}`}>
                                {log.statusCode}
                              </span>
                            </>
                          )}
                          {log.duration !== undefined && log.duration !== null && (
                            <>
                              <span className="text-gray-300">|</span>
                              <span>{formatDuration(log.duration)}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </button>

                    {/* Expanded detail */}
                    <AnimatePresence>
                      {isExpanded && <ExpandedDetail log={log} />}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            {/* Loading overlay for refreshes */}
            {isLoading && logs.length > 0 && (
              <div className="px-4 py-2 bg-blue-50 border-t border-blue-100 flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />
                <span className="text-xs text-blue-700">Refreshing...</span>
              </div>
            )}
          </Card>
        )}

        {/* ==================== Pagination ==================== */}
        {pagination.pages > 1 && (
          <nav className="flex items-center justify-between" aria-label="Activity logs pagination">
            <p className="text-sm text-gray-500">
              Showing{' '}
              <span className="font-medium text-gray-700">
                {(pagination.page - 1) * pagination.limit + 1}
              </span>
              {' - '}
              <span className="font-medium text-gray-700">
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>
              {' of '}
              <span className="font-medium text-gray-700">{(pagination.total ?? 0).toLocaleString()}</span>
            </p>

            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                disabled={currentPage <= 1}
                className="gap-1"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>

              {/* Page numbers */}
              <div className="hidden md:flex items-center gap-1">
                {Array.from({ length: Math.min(7, pagination.pages) }, (_, i) => {
                  let pageNum: number;
                  const totalPages = pagination.pages;

                  if (totalPages <= 7) {
                    pageNum = i + 1;
                  } else if (currentPage <= 4) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 3) {
                    pageNum = totalPages - 6 + i;
                  } else {
                    pageNum = currentPage - 3 + i;
                  }

                  if (pageNum < 1 || pageNum > totalPages) return null;

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setCurrentPage(pageNum)}
                      aria-label={`Go to page ${pageNum}`}
                      aria-current={currentPage === pageNum ? 'page' : undefined}
                      className={`
                        h-8 w-8 rounded-lg text-sm font-medium transition-all
                        ${currentPage === pageNum
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-100'
                        }
                      `}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              {/* Mobile page indicator */}
              <span className="md:hidden text-sm text-gray-500 px-2">
                Page {currentPage} of {pagination.pages}
              </span>

              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((prev) => Math.min(pagination.pages, prev + 1))}
                disabled={currentPage >= pagination.pages}
                className="gap-1"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </nav>
        )}

        {/* Footer info */}
        <div className="flex items-center justify-between text-xs text-gray-400 pb-4">
          <span>
            Last refreshed: {lastRefreshRef.current.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          {autoRefresh && (
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Auto-refreshing every {AUTO_REFRESH_INTERVAL / 1000}s
            </span>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ActivityLogsPage;
